import { AxiosError, AxiosResponse } from 'axios';
import instance, { ApiError } from '../../api/instance';
import {
  ContentTemplate,
  ContentTemplateCompany,
  ContentTemplateGroup,
  ContentTemplateVideos,
} from 'src/features/templates/templates.types';
import { Collection } from 'src/features/collections/collections.types';

const templatesApiPath = '/content/templates';

export type ContentQueryParams = {
  name?: string;
};

export const getTemplates = async ({
  params,
}: {
  params?: ContentQueryParams;
}): Promise<AxiosResponse<ContentTemplate[] & ApiError>> => {
  return instance
    .get<ContentTemplate[]>(templatesApiPath, { params })
    .catch(e => e.response as AxiosError<ApiError> as never);
};

export const createTemplate = async ({
  name,
}: {
  name: string;
}): Promise<AxiosResponse<ContentTemplate & ApiError>> => {
  return instance
    .post<ContentTemplate>(templatesApiPath, { name })
    .catch(e => e.response as AxiosError<ApiError> as never);
};

const templateApiPath = (id: string) => `/content/templates/${id}`;

export const getTemplate = async ({
  templateId,
}: {
  templateId: string;
}): Promise<AxiosResponse<ContentTemplate & ApiError>> => {
  return instance
    .get<ContentTemplate>(templateApiPath(templateId))
    .catch(e => e.response as AxiosError<ApiError> as never);
};

export const updateTemplate = async ({
  templateId,
  data,
}: {
  templateId: string;
  data: Partial<ContentTemplate>;
}): Promise<AxiosResponse<ContentTemplate & ApiError>> => {
  return instance
    .post<ContentTemplate>(templateApiPath(templateId), data)
    .catch(e => e.response as AxiosError<ApiError> as never);
};

export const deleteTemplate = async ({
  templateId,
}: {
  templateId: string;
}): Promise<AxiosResponse<ContentTemplate & ApiError>> => {
  return instance
    .delete<ContentTemplate>(templateApiPath(templateId))
    .catch(e => e.response as AxiosError<ApiError> as never);
};

const templateVideosApiPath = (id: string) => `/content/templates/${id}/videos`;

export const getTemplateCollections = async ({
  templateId,
}: {
  templateId: string;
}): Promise<AxiosResponse<ContentTemplateVideos & ApiError>> => {
  return instance
    .get<ContentTemplateVideos>(templateVideosApiPath(templateId))
    .catch(e => e.response as AxiosError<ApiError> as never);
};

type ids = {
  ids: string[];
};
export const addCollectionsToTemplate = async ({
  templateId,
  data,
}: {
  templateId: string;
  data: ids;
}): Promise<AxiosResponse<Collection[] & ApiError>> => {
  return instance
    .post<Collection[]>(templateVideosApiPath(templateId), data)
    .catch(e => e.response as AxiosError<ApiError> as never);
};

export const removeCollectionsFromTemplate = async ({
  templateId,
  data,
}: {
  templateId: string;
  data: ids;
}): Promise<AxiosResponse<void & ApiError>> => {
  return instance
    .delete(templateVideosApiPath(templateId), { data })
    .catch(e => e.response as AxiosError<ApiError> as never);
};

const templateVideosOrderApiPath = (id: string) => `${templateApiPath(id)}/videos/order`;

export const updateTemplateCollectionsOrder = async ({
  templateId,
  data,
}: {
  templateId: string;
  data: ids;
}): Promise<AxiosResponse<ids & ApiError>> => {
  return instance
    .post(templateVideosOrderApiPath(templateId), data)
    .catch(e => e.response as AxiosError<ApiError> as never);
};
const exportTemplateApiPath = (id: string) => `${templateApiPath(id)}/export`;

export const exportTemplateAPI = async ({
  templateId,
}: {
  templateId: string;
}): Promise<AxiosResponse<ids & ApiError>> => {
  return instance.get(exportTemplateApiPath(templateId)).catch(e => e.response as AxiosError<ApiError> as never);
};

const publishTemplateApiPath = (id: string) => `${templateApiPath(id)}/publish`;

export const publishTemplateAPI = async ({
  templateId,
}: {
  templateId: string;
}): Promise<AxiosResponse<ids & ApiError>> => {
  return instance.post(publishTemplateApiPath(templateId)).catch(e => e.response as AxiosError<ApiError> as never);
};

const templateCompaniesApiPath = (id: string) => `${templateApiPath(id)}/companies`;

export const getTemplateCompaniesAPI = async ({
  templateId,
}: {
  templateId: string;
}): Promise<AxiosResponse<ContentTemplateCompany & ApiError>> => {
  return instance
    .get<ContentTemplateCompany>(templateCompaniesApiPath(templateId))
    .catch(e => e.response as AxiosError<ApiError> as never);
};

export const addTemplateCompanyAPI = async ({
  templateId,
  data,
}: {
  templateId: string;
  data: { companyId: string };
}): Promise<AxiosResponse<ContentTemplateCompany & ApiError>> => {
  return instance
    .post<ContentTemplateCompany>(templateCompaniesApiPath(templateId), data)
    .catch(e => e.response as AxiosError<ApiError> as never);
};

const templateCompanyApiPath = (id: string, companyId: string) => `${templateApiPath(id)}/companies/${companyId}`;

export const removeCompanyFromTemplateAPI = async ({
  templateId,
  companyId,
}: {
  templateId: string;
  companyId: string;
}): Promise<AxiosResponse<void & ApiError>> => {
  return instance
    .delete(templateCompanyApiPath(templateId, companyId))
    .catch(e => e.response as AxiosError<ApiError> as never);
};

const templateGroupsApiPath = (id: string) => `${templateApiPath(id)}/groups`;

export const getTemplateGroupsAPI = async ({
  templateId,
}: {
  templateId: string;
}): Promise<AxiosResponse<ContentTemplateGroup & ApiError>> => {
  return instance
    .get<ContentTemplateGroup>(templateGroupsApiPath(templateId))
    .catch(e => e.response as AxiosError<ApiError> as never);
};

export const addGroupToTemplateAPI = async ({
  templateId,
  data,
}: {
  templateId: string;
  data: { groupId: string };
}): Promise<AxiosResponse<ContentTemplateGroup & ApiError>> => {
  return instance
    .post<ContentTemplateGroup>(templateGroupsApiPath(templateId), data)
    .catch(e => e.response as AxiosError<ApiError> as never);
};

const templateGroupApiPath = (id: string, groupId: string) => `${templateApiPath(id)}/groups/${groupId}`;

export const removeGroupFromTemplateAPI = async ({
  templateId,
  groupId,
}: {
  templateId: string;
  groupId: string;
}): Promise<AxiosResponse<void & ApiError>> => {
  return instance
    .delete(templateGroupApiPath(templateId, groupId))
    .catch(e => e.response as AxiosError<ApiError> as never);
};

interface GetCompanyOfflineTemplateResponse {
  template: ContentTemplate;
}

const companyOfflineTemplateApiPath = (companyId: string) => `/companies/${companyId}/offline-template`;

export const getCompanyOfflineTemplate = async ({
  companyId,
}: {
  companyId: string;
}): Promise<AxiosResponse<GetCompanyOfflineTemplateResponse | null>> => {
  return instance
    .get<GetCompanyOfflineTemplateResponse | null>(companyOfflineTemplateApiPath(companyId))
    .catch(e => e.response as AxiosError<ApiError> as never);
};

export const setCompanyOfflineTemplate = async ({
  companyId,
  templateId,
}: {
  companyId: string;
  templateId: string;
}): Promise<AxiosResponse<ContentTemplate>> => {
  return instance
    .post<ContentTemplate>(companyOfflineTemplateApiPath(companyId), { template_id: templateId })
    .catch(e => e.response as AxiosError<ApiError> as never);
};

export const removeCompanyOfflineTemplate = async ({
  companyId,
}: {
  companyId: string;
}): Promise<AxiosResponse<void>> => {
  return instance
    .delete(companyOfflineTemplateApiPath(companyId))
    .catch(e => e.response as AxiosError<ApiError> as never);
};

export const fetchTemplateOfflineCompaniesAPI = async ({ templateId }: { templateId: string }) => {
  return instance.get(`/content/templates/offline/${templateId}`);
};

export const deleteTemplateFromCompanyAPI = async ({
  templateId,
  companyId,
}: {
  templateId: string;
  companyId: string;
}) => {
  const response = await instance.delete(`/content/templates/offline/${templateId}`, {
    data: { company_id: companyId },
  });
  return response;
};

export const assignTemplateOfflineToCompanyAPI = async ({
  templateId,
  companyId,
}: {
  templateId: string;
  companyId: string;
}) => {
  const response = await instance.post(`/content/templates/offline/${templateId}`, {
    company_id: companyId,
  });
  return response;
};
